import { AnalysisResult } from '../types/analysis';
import { API_BASE_URL, MESSAGES } from '../constants';

export class AnalysisService {

  static async analyzeTranscript(
    transcript: string,
    file: File | null,
    salesStage: string,
    audioUrl: string = '',
    numSpeakers: number = 2,
    sessionId?: string
  ): Promise<AnalysisResult> {
    const formData = new FormData();
    
    if (file) {
      formData.append('file', file);
    }
    
    formData.append('transcript', transcript);
    formData.append('salesStage', salesStage);
    
    // Add sessionId if provided
    if (sessionId) {
      formData.append('sessionId', sessionId);
    }
    
    // Only send audioUrl if it's not empty (to avoid triggering async flow)
    console.log('🔥 AnalysisService: checking audioUrl:', audioUrl, 'trimmed:', audioUrl?.trim());
    if (audioUrl && audioUrl.trim()) {
      console.log('🔥 AnalysisService: adding audioUrl to FormData:', audioUrl);
      formData.append('audioUrl', audioUrl);
      formData.append('numSpeakers', numSpeakers.toString());
    } else {
      console.log('🔥 AnalysisService: audioUrl is empty, not adding to FormData');
    }

    const response = await fetch(`${API_BASE_URL}/analyze`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || MESSAGES.ERRORS.ANALYSIS_FAILED);
    }

    return response.json();
  }

  static async getJobStatus(jobId: string) {
    const response = await fetch(`${API_BASE_URL}/job/${jobId}/status`);

    if (!response.ok) {
      throw new Error(`Failed to get job status: ${response.status}`);
    }

    return response.json();
  }

  static async getTranscript(sessionId: string) {
    const response = await fetch(`${API_BASE_URL}/transcript/${sessionId}`);

    if (!response.ok) {
      throw new Error(`Failed to get transcript: ${response.status}`);
    }

    return response.json();
  }

  static async getAnalysis(id: string): Promise<AnalysisResult> {
    const response = await fetch(`${API_BASE_URL}/analysis/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Analysis not found');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to load analysis');
    }

    const responseData = await response.json();
    
    // Database response has nested structure: data.analysis contains the actual analysis
    // but we need to add the analysisId and transcript at the top level for frontend compatibility
    const analysisData = responseData.data.analysis;
    analysisData.analysisId = responseData.data.id;
    
    // Update metadata with salesStage from database if available
    if (responseData.data.salesStage) {
      if (!analysisData.metadata) {
        analysisData.metadata = {};
      }
      analysisData.metadata.salesStage = responseData.data.salesStage;
    }
    
    // Add transcript data if available (from database it's at data.transcript level)
    if (responseData.data.transcript) {
      // Parse transcript data for frontend - extract text content and create a transcript object
      const transcriptText = responseData.data.transcript;
      // Create a transcript object compatible with existing frontend code
      analysisData.transcript = {
        preview: transcriptText.substring(0, 200) + '...',
        fullText: transcriptText,
        fullLength: transcriptText.length,
        source: 'database',
        sessionId: responseData.data.id // Use analysis ID as session ID for database records
      };
    }
    
    return analysisData;
  }

  static async getSessionHistory(sessionId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/history/${sessionId}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to load session history');
    }

    const responseData = await response.json();
    return responseData.data || [];
  }
}